/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1642982400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jan 24 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "asana",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Tasks",
          "depth": 0,
          "id": "tasks"
        }, {
          "value": "Upsert",
          "depth": 1,
          "id": "upsert"
        }, {
          "value": "Update",
          "depth": 1,
          "id": "update"
        }, {
          "value": "Projects",
          "depth": 0,
          "id": "projects"
        }, {
          "value": "Array properties",
          "depth": 0,
          "id": "array-properties"
        }, {
          "value": "Subtasks",
          "depth": 0,
          "id": "subtasks"
        }, {
          "value": "Custom fields",
          "depth": 0,
          "id": "custom-fields"
        }, {
          "value": "Delete behavior",
          "depth": 0,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      img: "img",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Asana destination, Hightouch can upsert or update tasks, or update projects."
      }), "\n", _jsx(_components.p, {
        children: "For the task object, the destination supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": Upsert mode creates new tasks and updates existing tasks based on an external ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": Update mode updates existing or newly created tasks based on the Asana GID. The update mode can also update tasks created with Hightouch using the external ID."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For the projects object, the destination supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": Update mode updates existing projects based on the Asana GID."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Connect Asana via OAuth by granting permission to the Hightouch Asana Connect app."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-asana-grant.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tasks",
        children: "Tasks"
      }), "\n", _jsx(_components.h3, {
        id: "upsert",
        children: "Upsert"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can upsert tasks based on an external ID in Asana. This means it will update the task in Asana if it was previously created by Hightouch, otherwise it will create a new task. The external ID is unique to Hightouch thus in this mode it will only update tasks created via Hightouch."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Name"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Merge Rule"
            }), _jsx(_components.td, {
              align: "left",
              children: "The column to use as the external ID in Asana. This should be a unique identifier and will most likely be some form of ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of columns that will be pushed to tasks in Asana. Each time a sync occurs in Hightouch these columns will be updated. For array properties such as projects or followers, ", _jsx(_components.a, {
                href: "#array-properties",
                children: "refer to notes on array properties."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Custom Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of custom fields that will be pushed to tasks in Asana. ", _jsx(_components.a, {
                href: "#custom-fields",
                children: "Refer the notes on custom fields in Asana."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "update",
        children: "Update"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can update tasks based on the Asana GID. Hightouch can also update tasks based on an external ID if the task was created using Hightouch."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If upserting or updating via an external ID, Hightouch can only update tasks\ncreated with such an external ID through Hightouch, due to the way Asana\n", _jsx(_components.a, {
            href: "https://developers.asana.com/docs/custom-external-data",
            children: "external IDs are limited on a per application\nbasis"
          }), "."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Name"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Merge Rule"
            }), _jsx(_components.td, {
              align: "left",
              children: "The column that matches either the Asana GID or external ID in Asana."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of columns that will be pushed to tasks in Asana. Each time a sync occurs in Hightouch these columns will be updated. For array properties such as projects or followers, ", _jsx(_components.a, {
                href: "#array-properties",
                children: "refer to notes on array properties."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Custom Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of custom fields that will be pushed to tasks in Asana. ", _jsx(_components.a, {
                href: "#custom-fields",
                children: "Refer the notes on custom fields in Asana."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "projects",
        children: "Projects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can update projects on the Asana GID. Unfortunately, Hightouch can't upsert projects or update based on external ID because Asana doesn't yet support external metadata for objects other than projects."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Name"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Merge Rule"
            }), _jsx(_components.td, {
              align: "left",
              children: "The column that matches the Asana Project GID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of columns that will be pushed projects in Asana. Each time a sync occurs in Hightouch these columns will be updated. For array properties such as followers, ", _jsx(_components.a, {
                href: "#array-properties",
                children: "refer to notes on array properties."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Hightouch to Asana Custom Field Mappings"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A list of custom fields that will be pushed to projects in Asana. ", _jsx(_components.a, {
                href: "#custom-fields",
                children: "Refer the notes on custom fields in Asana."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "array-properties",
        children: "Array properties"
      }), "\n", _jsx(_components.p, {
        children: "Array properties can be passed to Asana by returning the data as either a comma delimited string or an array in your SQL query. Examples of array properties are:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Tasks: projects, followers, tags"
        }), "\n", _jsx(_components.li, {
          children: "Projects: followers"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In the SQL query, a column ", _jsx(_components.code, {
          children: "taskFollowers"
        }), "containing a string such as ", _jsx(_components.code, {
          children: "'1199948419683571,1185085322202311'"
        }), " can be mapped to the followers field of tasks in Asana."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-asana-array.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Using the example of followers, keep in mind that every type Hightouch runs a sync, it will check the followers assigned to the task in Asana, and:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add all followers that are present in ", _jsx(_components.code, {
            children: "taskFollowers"
          }), " and not present in Asana"]
        }), "\n", _jsxs(_components.li, {
          children: ["Remove all followers that are present in Asana and not present in ", _jsx(_components.code, {
            children: "taskFollowers"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "subtasks",
        children: "Subtasks"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.code, {
          children: "subtask"
        }), " is an Asana task object that is associated with a ", _jsx(_components.code, {
          children: "parent"
        }), " task object."]
      }), "\n", _jsxs(_components.p, {
        children: ["To create a ", _jsx(_components.code, {
          children: "subtask"
        }), ", select the column from your source that references ", _jsx(_components.code, {
          children: "parent"
        }), " tasks, then map it to the ", _jsx(_components.code, {
          children: "parent"
        }), " field in\nthe Mappings section."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-asana-subtask.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In your source, you can identify the ", _jsx(_components.code, {
          children: "parent"
        }), " task either by the external ID or the Asana GID. The external ID or the Asana GID should\nbe a string. Not passing any value in for the ", _jsx(_components.code, {
          children: "parent"
        }), " field will remove an existing parent-subtask association - the task will simply be considered a normal ", _jsx(_components.code, {
          children: "task"
        }), " instead of a ", _jsx(_components.code, {
          children: "subtask"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "custom-fields",
        children: "Custom fields"
      }), "\n", _jsx(_components.p, {
        children: "Custom fields are complex in Asana because they can be associated with both tasks and projects, and they can be restricted to only tasks in certain projects. To start, Hightouch supports custom fields that are available on a workspace level, which can be done by adding the field to the workspace's field library in Asana."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-asana-custom-fields.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Secondly, custom fields may cause errors if the field is not enabled in the project you are inserting the task into."
      }), "\n", _jsx(_components.h2, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "For the task object, you can choose what Hightouch's behavior is when rows leave your query results. The default is doing nothing."
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, you have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the task in Asana"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mark As Complete"
              })
            }), _jsx(_components.td, {
              children: "Keep the task but mark as complete"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete Destination Record"
              })
            }), _jsx(_components.td, {
              children: "Delete the task from Asana completely"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, you have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the task in Asana"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mark As Complete"
              })
            }), _jsx(_components.td, {
              children: "Keep the task but mark as complete"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
